/**
 * The push subscription's life on THIS device (v10.0.0). PURE: the browser's
 * PushManager and Ballast's API come in as arguments, so the lifecycle is
 * unit-tested without a browser (test/unit/push-client.test.ts).
 *
 * What WebKit actually does, checked 11 Oct 2026 against Apple's documentation
 * and WebKit's source (docs/SECURITY.md §13 has the sources):
 *
 *   - Push exists only for a web app opened from the Home Screen (iOS 16.4+).
 *     So a Safari tab is told to add Ballast to the Home Screen first, and is
 *     never offered a button that cannot work.
 *   - The permission prompt must come from a tap: subscribe() has to be the
 *     FIRST thing the tap handler awaits. A tap is good for one prompt, for a
 *     few seconds; an await on the network before it and the prompt is refused
 *     without being shown. `turnOn` therefore needs the key loaded beforehand.
 *   - Safari never fires `pushsubscriptionchange` (the handler exists in the
 *     IDL, so feature-detecting it lies). Subscriptions vanish silently, and
 *     Apple's push service has been seen answering 201 for one that is gone.
 *     So on every open, `ensureSubscription` asks the browser for its
 *     subscription and re-creates it if it is missing — and re-sends it either
 *     way, because the server may have dropped it after a 404/410.
 *   - Permission once granted lets subscribe() run without a tap, which is what
 *     makes the silent re-create possible. It never prompts on open.
 */

/** The parts of a PushSubscription this uses. */
export interface SubscriptionLike {
  readonly endpoint: string;
  readonly options?: { readonly applicationServerKey: ArrayBuffer | null };
  toJSON(): unknown;
  unsubscribe(): Promise<boolean>;
}

/** The parts of a PushManager this uses. */
export interface PushManagerLike {
  getSubscription(): Promise<SubscriptionLike | null>;
  subscribe(options: {
    userVisibleOnly: true;
    applicationServerKey: Uint8Array;
  }): Promise<SubscriptionLike>;
}

/** Ballast's side: store and forget this device's subscription. */
export interface PushServer {
  save(subscription: unknown): Promise<void>;
  remove(endpoint: string): Promise<void>;
}

/**
 * Whether the operator turned notifications on for THIS device. Kept in the
 * device's own storage: a phone the operator turned off must not be quietly
 * re-subscribed on its next open just because the OS permission still says yes.
 * Logout's Clear-Site-Data wipes it, with the subscription.
 */
export interface DeviceFlag {
  get(): boolean;
  set(on: boolean): void;
}

export type NotificationPermissionState = 'default' | 'granted' | 'denied';

export type PushStatus =
  /** The server cannot send (no field key). Nothing to offer. */
  | 'server-off'
  /** A browser tab, not the installed app: add to Home Screen first. */
  | 'not-installed'
  /** Installed, but this browser has no push. */
  | 'unsupported'
  /** The operator said no; only iOS Settings can undo it. */
  | 'blocked'
  /** Can be turned on with a tap. */
  | 'off'
  /** On for this device. */
  | 'on';

export function pushStatus(input: {
  serverAvailable: boolean;
  installed: boolean;
  supported: boolean;
  permission: NotificationPermissionState;
  subscribed: boolean;
}): PushStatus {
  if (!input.serverAvailable) return 'server-off';
  if (!input.installed) return 'not-installed';
  if (!input.supported) return 'unsupported';
  if (input.permission === 'denied') return 'blocked';
  return input.subscribed && input.permission === 'granted' ? 'on' : 'off';
}

export function base64UrlToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/** Was this subscription made with the server's CURRENT key? */
export function sameKey(subscription: SubscriptionLike, publicKey: string): boolean {
  const key = subscription.options?.applicationServerKey;
  // A browser that does not report the key: trust it rather than churn.
  if (!key) return true;
  const a = new Uint8Array(key);
  const b = base64UrlToBytes(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export type EnsureOutcome = 'not-wanted' | 'no-permission' | 'kept' | 'recreated' | 'rekeyed';

/**
 * On every app open. Never prompts. Re-creates a subscription the browser lost,
 * replaces one made with an old server key, and re-sends the current one.
 */
export async function ensureSubscription(deps: {
  pushManager: PushManagerLike;
  server: PushServer;
  publicKey: string;
  permission: NotificationPermissionState;
  flag: DeviceFlag;
}): Promise<EnsureOutcome> {
  if (!deps.flag.get()) return 'not-wanted';
  if (deps.permission !== 'granted') return 'no-permission';

  let sub = await deps.pushManager.getSubscription();
  let outcome: EnsureOutcome = 'kept';
  if (sub && !sameKey(sub, deps.publicKey)) {
    await sub.unsubscribe();
    sub = null;
    outcome = 'rekeyed';
  }
  if (!sub) {
    sub = await deps.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(deps.publicKey),
    });
    if (outcome === 'kept') outcome = 'recreated';
  }
  await deps.server.save(sub.toJSON());
  return outcome;
}

/**
 * From the tap. subscribe() is the first await, so the prompt it raises is
 * still inside the tap's activation (the key was loaded when Settings drew).
 * Throws what subscribe() throws — NotAllowedError when the operator says no.
 */
export async function turnOn(deps: {
  pushManager: PushManagerLike;
  server: PushServer;
  publicKey: string;
  flag: DeviceFlag;
}): Promise<void> {
  const sub = await deps.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToBytes(deps.publicKey),
  });
  await deps.server.save(sub.toJSON());
  deps.flag.set(true);
}

/** Turn this device off: Ballast forgets it first, then the browser does. */
export async function turnOff(deps: {
  pushManager: PushManagerLike;
  server: PushServer;
  flag: DeviceFlag;
}): Promise<void> {
  deps.flag.set(false);
  const sub = await deps.pushManager.getSubscription();
  if (!sub) return;
  await deps.server.remove(sub.endpoint);
  await sub.unsubscribe();
}
